// OperatorServiceTable.js

const OperatorServiceTable = ({ assessments, fieldOptions }) => {
  // Создаем карту для хранения данных по операторам и их услугам
  const operatorServiceMap = {};

  // Инициализируем карту для всех операторов из fieldOptions.operators
  if (fieldOptions?.operators && Array.isArray(fieldOptions.operators)) {
    fieldOptions.operators.forEach(operator => {
      const operatorFullName = `${operator.surname} ${operator.name}`;
      operatorServiceMap[operatorFullName] = {};
    });
  }

  // Заполняем карту данными об ошибках (score = 0)
  assessments.forEach(assessment => {
    const agentName = assessment.agent_name;
    const service = assessment.service;
    
    // Проверяем, что оператор и услуга существуют и есть ошибки
    if (agentName && service && operatorServiceMap[agentName] && assessment.items) {
      // Увеличиваем счетчик ошибок для данной услуги
      if (!operatorServiceMap[agentName][service]) {
        operatorServiceMap[agentName][service] = 0;
      }
      
      // Считаем количество критериев с оценкой 0
      const zeroScores = assessment.items.filter(item => item.score === 0).length;
      operatorServiceMap[agentName][service] += zeroScores;
    }
  });

  // Преобразуем карту в массив для сортировки
  const operatorServiceData = Object.entries(operatorServiceMap).map(([agentValue, services]) => {
    // Получаем текстовое название оператора
    const agentName = agentValue;
    
    // Преобразуем объект услуг в массив и сортируем по количеству ошибок
    const sortedServices = Object.entries(services)
      .map(([serviceValue, errorCount]) => {
        // Получаем текстовое название услуги
        const serviceOption = fieldOptions?.service?.find(opt => opt.option_value === serviceValue);
        const serviceName = serviceOption ? serviceOption.option_text : serviceValue;
        
        return {
          service: serviceName,
          errorCount: errorCount
        };
      })
      .sort((a, b) => b.errorCount - a.errorCount); // Сортируем по убыванию количества ошибок

    // Общее количество ошибок по всем услугам для данного оператора
    const totalErrorCount = sortedServices.reduce((sum, service) => sum + service.errorCount, 0);
    
    return {
      agent: agentName,
      services: sortedServices,
      totalErrorCount: totalErrorCount
    };
  });

  // Сортируем операторов по общему количеству ошибок (по убыванию)
  operatorServiceData.sort((a, b) => b.totalErrorCount - a.totalErrorCount);

  // Получаем топ-10 услуг с наибольшим количеством ошибок по всем операторам
  const topServicesSet = new Set();
  const serviceErrorMap = {};

  // Собираем все услуги и их общее количество ошибок
  operatorServiceData.forEach(operator => {
    operator.services.forEach(service => {
      if (!serviceErrorMap[service.service]) {
        serviceErrorMap[service.service] = 0;
      }
      serviceErrorMap[service.service] += service.errorCount;
    });
  });

  // Сортируем услуги по количеству ошибок и выбираем топ-10
  const topServices = Object.entries(serviceErrorMap)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 10)
    .map(([serviceName, errorCount]) => serviceName);

  // Фильтруем услуги у каждого оператора, оставляя только топ-10
  operatorServiceData.forEach(operator => {
    operator.services = operator.services.filter(service => 
      topServices.includes(service.service)
    );
  });

  // Определяем максимальную длину имени оператора для расчета ширины колонки
  const maxAgentLength = operatorServiceData.length > 0
    ? Math.max(...operatorServiceData.map(item => item.agent.length))
    : 0;
  const agentColumnWidth = Math.max(150, maxAgentLength * 8);

  // Определяем количество колонок
  const totalColumns = topServices.length + 2;

  return (
    <div className="table-wrapper" style={{ height: '80vh', display: 'flex', flexDirection: 'column' }}>
      <h3>Топ-10 услуг по операторам</h3>

      {/* Основной контейнер с обычной прокруткой */}
      <div
        style={{
          flex: 1,
          overflow: 'auto',
          position: 'relative'
        }}
      >
        {/* Таблица */}
        <table className="stats-table" style={{ minWidth: '100%', borderCollapse: 'collapse' }}>
          <thead style={{ position: 'sticky', top: 0, zIndex: 3 }}>
            <tr>
              <th style={{
                position: 'sticky', left: 0, zIndex: 3,
                minWidth: `${agentColumnWidth}px`, width: `${agentColumnWidth}px`, padding: '8px 15px'
              }}>Оператор</th>
              <th style={{
                position: 'sticky', left: `${agentColumnWidth}px`, zIndex: 3,
                minWidth: '80px', width: '80px',
                padding: '8px 4px'
              }}>Всего ошибок</th>
              {topServices.map(service => (
                <th
                  key={service}
                  style={{
                    minWidth: '150px',
                    maxWidth: '150px',
                    padding: '8px 4px',
                    wordWrap: 'break-word',
                    whiteSpace: 'normal',
                    verticalAlign: 'top',
                  }}
                >
                  {service}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {operatorServiceData.length === 0 ? (
              <tr>
                <td colSpan={totalColumns} style={{ textAlign: 'center' }}>Нет данных</td>
              </tr>
            ) : (
              operatorServiceData.map(operator => (
                <tr key={operator.agent}>
                  <td style={{
                    position: 'sticky', left: 0, zIndex: 2,
                    minWidth: `${agentColumnWidth}px`,
                    backgroundColor: 'white'
                  }}>{operator.agent}</td>
                  <td style={{
                    position: 'sticky', left: `${agentColumnWidth}px`, zIndex: 2,
                    minWidth: '80px',
                    backgroundColor: 'white'
                  }}>{operator.totalErrorCount}</td>
                  {topServices.map(serviceName => (
                    <td key={serviceName} style={{ minWidth: '150px', zIndex: 1 }}>
                      {operator.services.find(s => s.service === serviceName)?.errorCount || 0}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>

          <tfoot style={{ position: 'sticky', bottom: 0, backgroundColor: '#f5f5f5', zIndex: 3 }}>
            <tr>
              <td style={{
                position: 'sticky', left: 0, zIndex: 3,
                minWidth: `${agentColumnWidth}px`,
                backgroundColor: '#f5f5f5'
              }}>Grand Total</td>
              <td style={{
                position: 'sticky', left: `${agentColumnWidth}px`, zIndex: 3,
                minWidth: '80px',
                backgroundColor: '#f5f5f5'
              }}>
                {operatorServiceData.reduce((sum, operator) => sum + operator.totalErrorCount, 0)}
              </td>
              {topServices.map(serviceName => (
                <td key={serviceName}>
                  {operatorServiceData.reduce((sum, operator) => {
                    const service = operator.services.find(s => s.service === serviceName);
                    return sum + (service?.errorCount || 0);
                  }, 0)}
                </td>
              ))}
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

export default OperatorServiceTable;